import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material'
import React, { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CustomWindow from './../CustomWindow'

import DamagesOnVehicle from './../DamagesOnVehicle'
import DamageDetails from './DamageDetails'
import DamagesList from './DamagesList'

import useDamageDataQuery from '../../hooks/queries/useDamageDataQuery'
import useDamageInitialData from '../../hooks/vehicleDamages/useDamageInitialData'
import { useTranslation } from 'react-i18next'
import { Damage } from '../../constants'

const VehicleDamages = ({
  vehicleDataForCurrentSide,
  damages,
  tabValue,
}: {
  damages: Damage[]
  vehicleDataForCurrentSide: any
  tabValue: number
}) => {
  const {
    damageData = {},
    isOpenedDamageDetailsWindow,
    handleDamageDetailsWindowClose,
    getDamageData,
  } = useDamageDataQuery()

  const { t } = useTranslation()

  const [collapsed, setCollapsed] = useState(true)

  const [currentSlide, setCurrentSlide] = useState(1)
  const { damageTypes, damageDetailsTitle } = useDamageInitialData(
    tabValue,
    damageData,
    currentSlide
  )

  const placeholderImg =
    vehicleDataForCurrentSide?.placeholder &&
    vehicleDataForCurrentSide?.placeholder
      ? vehicleDataForCurrentSide.placeholder
      : vehicleDataForCurrentSide.defaultPlaceholder

  const afterChange = (index: number) => {
    setCurrentSlide(index + 1)
  }

  return (
    <Paper
      className="section"
      sx={(theme) => ({
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
        padding: theme.spacing(3, 2, 2),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        height: 320,
        ...(damages.length > 0 && { height: 'auto' }),
      })}
    >
      {!damages.length ? (
        <>
          <Box
            sx={(theme) => ({
              height: 160,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: theme.spacing(4),
            })}
          >
            <Box
              component={'img'}
              src={placeholderImg}
              alt="placeholder"
              sx={{
                maxHeight: '160px',
                maxWidth: '316px',
              }}
            />
          </Box>
          <Typography
            variant="h1"
            component="p"
            sx={{
              marginBottom: (theme) => theme.spacing(1.5),
            }}
          >
            {t('vehicle_damage.no_damage')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('vehicle_damage.in_case_damage_report')}.
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="h1"
            component="p"
            sx={{
              textAlign: 'left',
              width: '100%',
              margin: (theme) => theme.spacing(0, 0, 2),
            }}
          >
            {t('vehicle_damage.damage_list')}
          </Typography>
          {!isOpenedDamageDetailsWindow && (
            <DamagesOnVehicle
              damages={damages}
              handleDamagePinClick={getDamageData}
              vehiclePlaceholderImage={placeholderImg}
            />
          )}
          <Box
            width="calc(100% + 32px)"
            borderBottom={'1px solid #F1F1F1'}
            borderTop={'1px solid #F1F1F1'}
            display="flex"
            py={0.125}
            mx={-2}
            mb={1}
            px={2}
            height={52}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body1">
              {t('vehicle_damage.notifications')} ({damages.length})
            </Typography>
            {damages.length > 2 && (
              <IconButton onClick={() => setCollapsed(!collapsed)}>
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Box>
          <Box
            sx={(theme) => ({
              mx: theme.spacing(-2),
            })}
            width={'calc(100% + 32px)'}
          >
            <Collapse
              in={!collapsed}
              collapsedSize={144}
              sx={{ maxWidth: '100%' }}
            >
              <DamagesList
                damages={damages}
                handleDamageListItemClick={getDamageData}
                damageTypes={damageTypes}
              />
            </Collapse>
          </Box>
        </>
      )}

      <CustomWindow
        title={damageDetailsTitle}
        open={isOpenedDamageDetailsWindow}
        handleClose={handleDamageDetailsWindowClose}
        noPadding
        icon="close"
      >
        <DamageDetails
          damageData={damageData}
          placeholder={vehicleDataForCurrentSide.placeholder}
          defaultPlaceholder={vehicleDataForCurrentSide.defaultPlaceholder}
          damages={damages}
          damageTypes={damageTypes}
          afterChange={afterChange}
        />
      </CustomWindow>
    </Paper>
  )
}

export default VehicleDamages
